/**
 * Signup — cadastro freemium (SaaS v4).
 * Cria o escritório (plano free) + usuário fundador e auto-loga (cookie JWT).
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../hooks';

const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB',
  'PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

export function Signup() {
  const navigate = useNavigate();
  const { setLogin } = useAuth();
  const [form, setForm] = useState({
    nome: '', email: '', senha: '',
    escritorio_nome: '', oab_numero: '', oab_uf: 'RJ',
  });
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (form.senha.length < 8) { setErro('A senha precisa de ao menos 8 caracteres.'); return; }
    if (!/^\d{4,6}$/.test(form.oab_numero.replace(/\D/g, ''))) {
      setErro('Número de OAB inválido (4 a 6 dígitos).'); return;
    }
    setEnviando(true);
    try {
      const r = await api.signup({
        ...form,
        oab_numero: form.oab_numero.replace(/\D/g, ''),
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        escritorio_nome: form.escritorio_nome.trim(),
      });
      // auto-login: o cookie JWT já foi setado pelo backend
      setLogin({ id: (r as any).user_id ?? 0, nome: r.nome, papel: 'admin', precisa_trocar_senha: false });
      navigate('/semanal', { replace: true });
    } catch (e: any) {
      setErro(e?.message ?? 'Falha no cadastro — tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  const inputCls =
    'w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 ' +
    'placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="max-w-6xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white">A</div>
          <span className="font-display font-bold text-lg tracking-tight">Agenda de Prazos · TRT-1</span>
        </Link>
        <Link to="/login" className="text-sm text-slate-400 hover:text-white transition">Já tenho conta →</Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <form onSubmit={onSubmit} className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/60 p-8 space-y-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Criar conta grátis</h1>
            <p className="mt-1 text-sm text-slate-400">
              Monitore os prazos da OAB do seu escritório no kanban. Sem cartão de crédito.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Seu nome</label>
            <input required value={form.nome} onChange={(e) => set('nome', e.target.value)}
              className={inputCls} placeholder="Maria da Silva" autoComplete="name" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Email</label>
            <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              className={inputCls} placeholder="voce@escritorio.adv.br" autoComplete="email" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Senha (mín. 8 caracteres)</label>
            <input required type="password" minLength={8} value={form.senha} onChange={(e) => set('senha', e.target.value)}
              className={inputCls} placeholder="••••••••" autoComplete="new-password" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Nome do escritório</label>
            <input required value={form.escritorio_nome} onChange={(e) => set('escritorio_nome', e.target.value)}
              className={inputCls} placeholder="Silva & Associados" />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-slate-400 mb-1">OAB (número)</label>
              <input required inputMode="numeric" value={form.oab_numero}
                onChange={(e) => set('oab_numero', e.target.value)}
                className={inputCls} placeholder="123456" />
            </div>
            <div className="w-28">
              <label className="block text-xs font-semibold text-slate-400 mb-1">UF</label>
              <select value={form.oab_uf} onChange={(e) => set('oab_uf', e.target.value)} className={inputCls}>
                {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
          </div>

          {erro && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {erro}
            </div>
          )}

          <button type="submit" disabled={enviando}
            className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold transition">
            {enviando ? 'Criando…' : 'Criar conta e entrar'}
          </button>

          <p className="text-[11px] text-slate-500 leading-snug">
            Os prazos do DJEN da OAB informada começam a aparecer na próxima coleta automática.
            Conexão atual sem HTTPS — não reutilize senhas de outros serviços.
          </p>
        </form>
      </main>
    </div>
  );
}
